import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Modal from '../../../../shared/_TBComponents/Modal';
import Button from '../../../../shared/_TBComponents/Button';
import Input from '../../../../shared/_TBComponents/Input';
import SelectBox from '../../../../shared/_TBComponents/SelectBox';
import ModalHeading from './ModalHeading';
import { showErrorToast } from '../../../../shared/utils/toastManager';
import {
  updateSavedWorkoutInfo,
  fetchAllProgramTags,
  createProgramTag,
  uploadProgramHeaderImage,
} from '../../../calendar/ducks/calendarActions';

// Renames a program and edits its description/tags/header image. Removing an
// image isn't supported yet — the PUT can't null out mediaId, add/replace only.

// The shared Modal's content wrapper is a flex row; without width:100% this child
// shrinks to its intrinsic width and leaves dead space on the right.
const Body = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 18px;
  width: 100%;
`;

const FieldLabel = styled.span`
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  color: #444444;
`;

const FieldHint = styled.span`
  font-family: "Nunito Sans";
  font-size: 12px;
  color: #9E9E9E;
`;

const ImageUploadArea = styled.div`
  width: 100%;
  height: 120px;
  border: 1px dashed ${(props) => (props.isDragActive ? '#444444' : '#EEEEEE')};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.isDragActive ? 'rgba(68, 68, 68, 0.05)' : 'white')};
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #444444;
    background: rgba(68, 68, 68, 0.05);
  }
`;

const ImagePreviewContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F5F5F5;
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const UploadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #444444;
`;

const ImageUploadText = styled.div`
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #444444;
`;

const FileTypeText = styled.div`
  font-family: "Nunito Sans";
  font-size: 12px;
  color: #444444;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

const EditProgramModal = ({
  isOpen, onClose, program, accountCode,
}) => {
  const dispatch = useDispatch();
  const tagsForAllPrograms = useSelector((state) => state.calendar.data.tagsForAllPrograms);

  const [programName, setProgramName] = useState('');
  const [description, setDescription] = useState('');
  const [tagIds, setTagIds] = useState([]);
  const [tagsMenuOpen, setTagsMenuOpen] = useState(false);
  // headerImage carries { preview, mediaUrl, mediaId }. mediaUrl is null while an
  // upload is in flight; a seeded (existing) image has it set from the start.
  const [headerImage, setHeaderImage] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isNewTagLoading, setIsNewTagLoading] = useState(false);
  const [tagsError, setTagsError] = useState(false);

  // Tracks which program an in-flight image upload belongs to, so a response
  // that lands after the modal switched programs can't write into the wrong form.
  const programIdRef = useRef(null);

  // Hydrate the form whenever a program is opened for editing.
  useEffect(() => {
    if (isOpen && program) {
      if (programIdRef.current !== program.id) setIsUploading(false);
      programIdRef.current = program.id;
      setProgramName(program.programName || '');
      setDescription(program.description || '');
      setTagIds((program.tags || []).map((t) => t.id));
      setHeaderImage(program.media?.id
        ? {
          preview: program.media.mediaUrl,
          mediaUrl: program.media.mediaUrl,
          mediaId: program.media.id,
        }
        : null);
      setUploadError(null);
    }
  }, [isOpen, program]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setUploadError('Please upload a valid image file');
      return;
    }
    const file = acceptedFiles[0];
    setUploadError(null);
    setHeaderImage({ file, preview: URL.createObjectURL(file), mediaUrl: null });

    const uploadProgramId = programIdRef.current;
    setIsUploading(true);
    dispatch(uploadProgramHeaderImage(file, {
      onSuccess: (data) => {
        if (programIdRef.current !== uploadProgramId) return;
        setIsUploading(false);
        setHeaderImage((prev) => (prev
          ? { ...prev, mediaUrl: data.mediaUrl, mediaId: data.id }
          : null));
      },
      onError: () => {
        if (programIdRef.current !== uploadProgramId) return;
        setIsUploading(false);
        setUploadError('Failed to upload image');
        setHeaderImage(null);
      },
    }));
  }, [dispatch]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png',
    maxFiles: 1,
    multiple: false,
  });

  // Make sure tag options are loaded for the picker.
  useEffect(() => {
    if (isOpen && accountCode && (!tagsForAllPrograms || tagsForAllPrograms.length === 0)) {
      setTagsError(false);
      dispatch(fetchAllProgramTags(accountCode, {
        onError: () => setTagsError(true),
      }));
    }
  }, [isOpen, accountCode]);

  const tagOptions = useMemo(
    () => [...(tagsForAllPrograms || [])]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((tag) => ({ id: tag.id, name: tag.name })),
    [tagsForAllPrograms],
  );

  const selectedTagOptions = tagIds
    .map((id) => tagOptions.find((o) => o.id === id))
    .filter(Boolean);

  const handleSave = () => {
    const trimmed = programName.trim();
    if (!trimmed) {
      showErrorToast('Program name is required');
      return;
    }
    if (headerImage && !headerImage.mediaUrl) {
      showErrorToast('Image is still uploading. Please wait.');
      return;
    }
    setIsSaving(true);
    // Full-replace PUT: numberOfDays is required even though editing can't change
    // it, and mediaId must be OMITTED (not null) — the API schema types it non-nullable.
    const payload = {
      programName: trimmed,
      description,
      saveInfoTagIds: tagIds,
      numberOfDays: program.numberOfDays,
    };
    if (headerImage?.mediaId) payload.mediaId = headerImage.mediaId;
    dispatch(updateSavedWorkoutInfo(
      accountCode,
      program.id,
      payload,
      {
        onSuccess: () => { setIsSaving(false); onClose(); },
        onError: () => setIsSaving(false),
      },
    ));
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      hideTitle
      customMaxWidth='480px'
      customMinHeight='0'
      customPadding='32px'
      customBorderRadius='8px'
      hideButton
    >
      {program && (
        <Body>
          <ModalHeading title='Edit Program' onClose={onClose} />
          <Field>
            <FieldLabel>Program name</FieldLabel>
            <Input
              name='edit-program-name'
              inputOnly
              value={programName}
              placeholder='Program name'
              onChange={(e) => setProgramName(e.target.value)}
              styledPropsInput={{ width: '100%', height: '40px', fontSize: '14px' }}
            />
          </Field>

          <Field>
            <FieldLabel>Description</FieldLabel>
            <Input
              name='edit-program-description'
              inputOnly
              isTextArea
              value={description}
              placeholder='Optional description'
              onChange={(e) => setDescription(e.target.value)}
              styledPropsInput={{ width: '100%', minHeight: '80px', fontSize: '14px' }}
            />
          </Field>

          <Field>
            <FieldLabel>Header image</FieldLabel>
            <FieldHint>Recommended size: 1060 x 420</FieldHint>
            <ImageUploadArea {...getRootProps()} isDragActive={isDragActive}>
              <input {...getInputProps()} />
              {headerImage ? (
                <ImagePreviewContainer>
                  <ImagePreview src={headerImage.preview} alt='Header preview' />
                  {isUploading && <UploadingOverlay>Uploading...</UploadingOverlay>}
                </ImagePreviewContainer>
              ) : (
                <>
                  <ImageUploadText>Click or drag image here.</ImageUploadText>
                  <FileTypeText>Accepted file types: jpg, jpeg, png</FileTypeText>
                </>
              )}
            </ImageUploadArea>
            {uploadError && (
              <FieldHint style={{ color: '#E53935' }}>{uploadError}</FieldHint>
            )}
          </Field>

          <Field
            // react-select doesn't stop propagation on Escape, so without this
            // react-modal would also dismiss the form and lose the user's input.
            onKeyDown={(e) => {
              if (e.key === 'Escape' && tagsMenuOpen) e.stopPropagation();
            }}
          >
            <SelectBox
              title='Tags (optional)'
              titleStyledProps={{ fontSize: '14px', fontWeight: 700 }}
              options={tagOptions}
              value={selectedTagOptions}
              isMulti
              isCreateable
              isLoading={isNewTagLoading}
              isDisabled={isNewTagLoading}
              onMenuOpen={() => setTagsMenuOpen(true)}
              onMenuClose={() => setTagsMenuOpen(false)}
              onChangeFunction={(selected) => setTagIds((selected || []).map((o) => o.id))}
              onCreateOption={(name) => {
                setIsNewTagLoading(true);
                dispatch(createProgramTag(accountCode, name, {
                  onSuccess: (newTag) => {
                    setTagIds((prev) => [...prev, newTag.id]);
                    setIsNewTagLoading(false);
                  },
                  onError: () => {
                    showErrorToast('Unable to create new tag.');
                    setIsNewTagLoading(false);
                  },
                }));
              }}
              noOptionsMessage='Type to add a new tag'
            />
            {tagsError && (
              <FieldHint style={{ color: '#E53935' }}>Unable to load tags</FieldHint>
            )}
          </Field>

          <Footer>
            <Button
              name='Cancel'
              bgColor='#FFF'
              borderColor='#EEE'
              txtColor='#444444'
              customBorderRadius='4px'
              fontSize='14px'
              styledTextProps={{ fontSize: '14px', fontWeight: 700 }}
              onClick={onClose}
            />
            <Button
              name='Save'
              bgColor='#444444'
              borderColor='#444444'
              txtColor='#FFF'
              customBorderRadius='4px'
              fontSize='14px'
              isLoading={isSaving}
              disabled={isSaving || isUploading}
              styledTextProps={{ fontSize: '14px', fontWeight: 700 }}
              onClick={handleSave}
            />
          </Footer>
        </Body>
      )}
    </Modal>
  );
};

EditProgramModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountCode: PropTypes.number,
  program: PropTypes.shape({
    id: PropTypes.number,
    programName: PropTypes.string,
    description: PropTypes.string,
    numberOfDays: PropTypes.number,
    tags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    media: PropTypes.shape({ id: PropTypes.number, mediaUrl: PropTypes.string }),
  }),
};

EditProgramModal.defaultProps = {
  accountCode: undefined,
  program: null,
};

export default EditProgramModal;
